import React, { useRef, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { authService } from "../../../utils/authService";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import axiosInstance from "../../../utils/axiosConfig";
import { useTranslation } from "react-i18next";

export const SignatureSection = ({
  selectedSchedule,
  currentUser,
  setSelectedSchedule,
  handleCompleteService,
}) => {
  const { t } = useTranslation();
  const sigPad = useRef({});
  const containerRef = useRef(null);

  useEffect(() => {
    const resizeCanvas = () => {
      const canvas = sigPad.current?._canvas;
      const container = containerRef.current;
      if (canvas && container) {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = container.offsetWidth * ratio;
        canvas.height = 200 * ratio;
        canvas.getContext("2d").scale(ratio, ratio);
        canvas.style.width = `${container.offsetWidth}px`;
        canvas.style.height = "200px";
      }
    };

    window.addEventListener("resize", resizeCanvas);
    // Initial resize
    setTimeout(resizeCanvas, 0);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, []);

  const handleDeleteSignature = async () => {
    const response = await axiosInstance.delete(
      `/schedules/${selectedSchedule.id}/signature`
    );
    const updatedSchedule = response.data;
    setSelectedSchedule(updatedSchedule);
  };

  const handleSignatureSave = async (signatureBlob) => {
    const formData = new FormData();
    formData.append(
      "schedule[client_signature]",
      signatureBlob,
      "signature.png"
    );
    const response = await axiosInstance.patch(
      `/schedules/${selectedSchedule.id}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    const updatedSchedule = response.data;
    setSelectedSchedule(updatedSchedule);
  };

  const clear = () => {
    sigPad.current.clear();
  };

  const save = () => {
    if (!sigPad.current.isEmpty()) {
      const signatureData = sigPad.current.toDataURL();
      const blobData = dataURLtoBlob(signatureData);
      handleSignatureSave(blobData);
    }
  };

  // Helper function para convertir dataURL a Blob
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleStartService = async () => {
    try {
      const response = await axiosInstance.patch(
        `/schedules/${selectedSchedule.id}/start_service`
      );
      setSelectedSchedule(response.data);
    } catch (error) {
      console.error("Error starting service:", error);
    }
  };

  const handleCompleteServiceTime = async () => {
    try {
      const token = authService.getToken();
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/schedules/${selectedSchedule.id}/complete_service`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedSchedule(response.data);
    } catch (error) {
      console.error("Error completing service:", error);
    }
  };

  const handleOutOfService = async () => {
    try {
      const token = authService.getToken();
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/schedules/${selectedSchedule.id}/out_of_service`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedSchedule(response.data);
    } catch (error) {
      console.error("Error setting schedule out of service:", error);
    }
  };

  const isServiceComplete = () => {
    const hasMinimumPhotos = selectedSchedule?.photos_urls?.length >= 3;
    return (
      hasMinimumPhotos &&
      selectedSchedule?.document &&
      selectedSchedule?.signature_url &&
      selectedSchedule?.person_name
    );
  };

  return (
    <Box sx={{ mt: 3, width: "100%" }}>
      <Typography variant="h6">{t("clientSignature")}</Typography>
      {selectedSchedule?.signature_url ? (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: "800px",
            margin: "0 auto",
          }}
        >
          <img
            src={selectedSchedule.signature_url}
            alt={t("clientSignature")}
            style={{ width: "100%", height: "auto", display: "block" }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "flex",
              gap: 1,
              backgroundColor: "rgba(255,255,255,0.8)",
              borderRadius: "4px",
              padding: "4px",
            }}
          >
            <IconButton
              size="small"
              color="error"
              onClick={handleDeleteSignature}
              disabled={
                (selectedSchedule?.status === "completed" &&
                  currentUser !== "admin") ||
                (currentUser !== "admin" &&
                  !selectedSchedule.service_started_at)
              }
            >
              <DeleteIcon />
            </IconButton>
            <IconButton
              size="small"
              color="primary"
              onClick={() =>
                setSelectedSchedule({
                  ...selectedSchedule,
                  signature_url: null,
                })
              }
              disabled={
                (selectedSchedule?.status === "completed" &&
                  currentUser !== "admin") ||
                (currentUser !== "admin" &&
                  !selectedSchedule.service_started_at)
              }
            >
              <EditIcon />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box
          ref={containerRef}
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            position: "relative",
            width: "100%",
            maxWidth: "800px",
            margin: "0 auto",
          }}
        >
          <SignatureCanvas
            ref={sigPad}
            canvasProps={{
              className: "signature-canvas",
              style: { width: "100%", height: "200px" },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "flex",
              gap: 1,
              backgroundColor: "rgba(255,255,255,0.8)",
              borderRadius: "4px",
              padding: "4px",
            }}
          >
            <IconButton
              size="small"
              color="default"
              onClick={clear}
              disabled={
                (selectedSchedule?.status === "completed" &&
                  currentUser !== "admin") ||
                (currentUser !== "admin" &&
                  !selectedSchedule?.service_started_at)
              }
            >
              <ClearIcon />
            </IconButton>
            <IconButton
              size="small"
              color="primary"
              onClick={save}
              disabled={
                (selectedSchedule?.status === "completed" &&
                  currentUser !== "admin") ||
                (currentUser !== "admin" &&
                  !selectedSchedule?.service_started_at)
              }
            >
              <SaveIcon />
            </IconButton>
          </Box>
        </Box>
      )}
      <Box sx={{ mb: 2 }}>
        <TextField
          label={t("document")}
          value={selectedSchedule.document || ""}
          onChange={(e) =>
            setSelectedSchedule({
              ...selectedSchedule,
              document: e.target.value,
            })
          }
          fullWidth
          margin="normal"
        />
        <TextField
          label={t("personName")}
          value={selectedSchedule.person_name || ""}
          onChange={(e) =>
            setSelectedSchedule({
              ...selectedSchedule,
              person_name: e.target.value,
            })
          }
          fullWidth
          margin="normal"
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const formData = new FormData();
            formData.append(
              "schedule[document]",
              selectedSchedule.document || ""
            );
            formData.append(
              "schedule[person_name]",
              selectedSchedule.person_name || ""
            );
            formData.append("schedule[client_signature]", "");
            axiosInstance
              .patch(`/schedules/${selectedSchedule.id}`, formData)
              .then((response) => setSelectedSchedule(response.data));
          }}
          sx={{ mt: 1 }}
        >
          {t("saveInformation")}
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          padding: "20px",
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          {!selectedSchedule?.service_started_at &&
            selectedSchedule?.status !== "completed" && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleStartService}
              >
                {t("startService")}
              </Button>
            )}
          {selectedSchedule?.service_started_at &&
            selectedSchedule?.status !== "completed" &&
            selectedSchedule?.status !== "out_of_service" && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  disabled={!isServiceComplete()}
                  onClick={() => {
                    handleCompleteServiceTime();
                    handleCompleteService();
                  }}
                >
                  {t("completeService")}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleOutOfService}
                >
                  {t("outOfService")}
                </Button>
              </>
            )}
        </Box>
        {selectedSchedule?.status === "completed" && (
          <Typography color="success.main" variant="h6">
            {t("serviceCompleted")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
